import { useState, useEffect } from 'react';
import { isValid, format, differenceInDays, parseISO } from 'date-fns';

interface ReminderModalProps {
  onClose: () => void;
}

interface Reminder {
  _id: string;
  title: string;
  closeDate: string;
  status: string;
}

const ReminderModal = ({ onClose }: ReminderModalProps) => {
  const [upcomingReminders, setUpcomingReminders] = useState<Reminder[]>([]);

  useEffect(() => {
    fetchGrantData();
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const fetchGrantData = async () => {
    try {
      const response = await fetch(`https://academichub-backend.onrender.com/grants/get`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch grants');
      }

      const data = await response.json();
      const reminders = findReminders(data);
      setUpcomingReminders(reminders);
    } catch (error) {
      console.error('Error fetching grants:', error);
    }
  };

  const findReminders = (grants: Reminder[]) => {
    return grants.filter(grant => {
      if (!grant.closeDate) return false;

      const closeDate = parseISO(grant.closeDate);
      if (!isValid(closeDate)) return false;

      const daysUntilClose = differenceInDays(closeDate, new Date());

      return grant.status === 'draft' && daysUntilClose >= 0 && daysUntilClose <= 10;
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl font-bold"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="text-xl font-bold mb-4">Reminders</h2>
        {upcomingReminders.length > 0 ? (
          <div className="space-y-4">
            {upcomingReminders.map(grant => {
              const closeDate = parseISO(grant.closeDate);
              const daysLeft = differenceInDays(closeDate, new Date());

              return (
                <div
                  key={grant._id}
                  className="border border-gray-300 rounded-lg p-4 bg-gray-50 shadow-sm"
                >
                  <h3 className="text-md font-bold text-[#1d244f] mb-1">{grant.title}</h3>
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    {daysLeft} day{daysLeft !== 1 ? 's' : ''} to go
                  </p>
                  <div className="border border-gray-200 bg-white rounded-md px-4 py-2">
                    <span className="text-sm text-gray-700">
                      Close Date:{' '}
                      <span className="text-red-600 font-medium">
                        {format(closeDate, 'do MMM yyyy')}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500">No upcoming deadlines.</p>
        )}
      </div>
    </div>
  );
};

export default ReminderModal;
